import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { iconStudentDropdown } from '../../assets';
import { useTheme } from '../../theme';
import { HeaderIconPerson } from '../icons/HeaderIconPerson';

export interface StudentCoursePlayerHeaderProps {
  backLink: ReactNode;
  academyName: string;
  academySubtitle: string;
  searchPlaceholder?: string;
  userName: string;
  userEmail: string;
  /** Exibe o chevron ao lado do bloco do usuário (visual do menu). Default: true */
  showUserChevron?: boolean;
}

export function StudentCoursePlayerHeader({
  backLink,
  academyName,
  academySubtitle,
  searchPlaceholder,
  userName,
  userEmail,
  showUserChevron = true,
}: StudentCoursePlayerHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const borderBottom = theme.palette.filterBox?.border ?? '#E0E0E0';
  const headerTokens = theme.palette.studentCoursePlayer.header;
  const placeholderClass = 'student-course-player-header-search-ph';
  const effectiveSearchPlaceholder =
    searchPlaceholder || t('student_course_player.header.search_placeholder');

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.lg,
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        height: headerTokens.height,
        boxSizing: 'border-box',
        padding: `0 ${theme.spacing.xl}`,
        backgroundColor: theme.palette.background.paper,
        border: 'none',
        borderBottom: `1px solid ${borderBottom}`,
        fontFamily: theme.typography.fontFamily,
      }}
    >
      <style>{`
        .${placeholderClass}::placeholder {
          color: ${theme.palette.input.placeholder};
          opacity: 1;
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.md,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <div style={{ color: theme.palette.text.primary, flexShrink: 0 }}>
          {backLink}
        </div>
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.palette.text.primary,
              lineHeight: theme.typography.lineHeight.tight,
            }}
          >
            {academyName}
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 2,
              color: theme.palette.text.secondary,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.normal,
            }}
          >
            {academySubtitle}
          </p>
        </div>
      </div>

      {/* <div
        style={{
          position: 'relative',
          flex: '1 1 auto',
          minWidth: 120,
          maxWidth: 560,
          marginLeft: theme.spacing.md,
          marginRight: theme.spacing.md,
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: theme.spacing.md,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            pointerEvents: 'none',
          }}
        >
          <img
            src={iconStudentSearchHeader}
            alt=""
            width={18}
            height={18}
            aria-hidden
            style={{ display: 'block', objectFit: 'contain' }}
          />
        </span>
        <input
          type="search"
          className={placeholderClass}
          placeholder={effectiveSearchPlaceholder}
          aria-label={effectiveSearchPlaceholder}
          style={{
            width: '100%',
            height: 40,
            borderRadius: 9999,
            border: `1px solid ${borderBottom}`,
            paddingLeft: '2.35rem',
            paddingRight: theme.spacing.md,
            fontSize: theme.typography.fontSize.sm,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />
      </div> */}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm2,
          flexShrink: 0,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 36,
            borderRadius: theme.shape.borderRadius.full,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <HeaderIconPerson />
        </div>
        <div style={{ minWidth: 0, textAlign: 'left' }}>
          <p
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.palette.text.primary,
              whiteSpace: 'nowrap',
            }}
          >
            {userName}
          </p>
          <p
            style={{
              margin: 0,
              marginTop: 2,
              fontSize: theme.typography.fontSize.xs,
              color: theme.palette.text.secondary,
              whiteSpace: 'nowrap',
            }}
          >
            {userEmail}
          </p>
        </div>
        {showUserChevron ? (
          <span
            style={{
              display: 'flex',
              flexShrink: 0,
              alignItems: 'center',
            }}
          >
            <img
              src={iconStudentDropdown}
              alt=""
              width={12}
              height={6}
              aria-hidden
              style={{ display: 'block', objectFit: 'contain' }}
            />
          </span>
        ) : null}
      </div>
    </header>
  );
}
